// =====================================================
// DASHBOARD STATS — Suivi des appels par équipage (RUN 5)
// =====================================================
// Section « Suivi des appels (pour programmer les vagues de relance) » du
// cadrage 07/07. Fonctions pures, testables en vitest, sans dépendance React.
//
// BESOIN MÉTIER (Maxime) : savoir combien de prospects / BPI ont reçu 0 / 1 / 2 /
// 3+ appels, POUR PROGRAMMER LES VAGUES DE RELANCE. Le bucket « 0 appel » (absent
// de la wireframe qui démarre à 1) est LE besoin : les gens jamais contactés.
//
// ── ATTRIBUTION DES APPELS (schéma RÉEL probé desertours-dev) ─────────────────
//   ⚠️ Sur desertours-dev, les 40 call_logged sont TOUS rattachés à un CONTACT
//   (entity_type='contact', entity_id=contact.id) — AUCUN sur un crew. L'unité de
//   relance est donc le CONTACT (la personne qu'on appelle), pas l'équipage :
//   un équipage = 1-2 contacts, et c'est le contact qui porte l'historique
//   d'appels. On compte les appels par contact.id. metadata JSONB =
//   {direction (canal), outcome (statut), duration, notes} — ⚠️ direction
//   quasi-vide sur le seed (0/40), outcome renseigné (40/40).
//
// ── UNIVERS = table de RELANCE (contacts encore à relancer) ──────────────────
//   On ne compte QUE les contacts qui ont encore besoin d'être relancés :
//     • BPI/PR : prospects (contacts.status='prospect', GLOBAUX) + contacts
//                membres d'un équipage 'bpi_payment_pending' (BPI payé,
//                pré-inscription pas validée) de l'édition sélectionnée.
//     • PI     : contacts membres d'un équipage 'pre_registered' (pré-inscrit,
//                pas encore inscrit déf) de l'édition sélectionnée.
//   EXCLUS (aucune relance nécessaire, documenté dans l'UI) : membres d'équipages
//   inscrits définitifs (registered/completed/bed_*), résiliés, reportés,
//   archivés. Leurs appels sont comptés « hors périmètre relance » (honnêteté).
//
// ── PÉRIODE ──────────────────────────────────────────────────────────────────
//   La relance est un ÉTAT À DATE : on compte les appels EN CUMUL (tout
//   l'historique), pas fenêtré — sinon « 0 appel » sur 7 jours serait trompeur.
//   Le filtre Édition scope les équipages ; le filtre Période n'agit pas ici
//   (documenté). Les prospects sont GLOBAUX (pas de produit — cf. nomenclature).

import type {
  CallDirection,
  CallOutcome,
  EditionRef,
  RawCall,
  RawContact,
  RawCrew,
  RawMemberContact,
  RawRows,
  RawUser,
} from './types';

/** Segment de tunnel d'un contact relançable. */
export type AppelSegment = 'bpi_pr' | 'pi';

/** Statut crew « BPI payé, pas encore pré-inscrit » → segment BPI/PR. */
const BPI_PENDING_STATUS = 'bpi_payment_pending';
/** Statut crew « pré-inscrit, pas encore inscrit déf » → segment PI. */
const PI_ONLY_STATUS = 'pre_registered';

/** Un appel du détail dépliable (une relance). */
export interface CallDetail {
  id: string;
  /** created_at de l'appel. */
  date: string;
  /** Canal = direction (Entrant/Sortant), null si non renseigné. */
  direction: CallDirection | null;
  /** Statut = outcome (Répondu/Non abouti/Répondeur), null si non renseigné. */
  outcome: CallOutcome | null;
  /** Commercial ayant consigné l'appel (nom résolu) ou null. */
  commercialName: string | null;
  /** Libellé de l'entité appelée (à qui s'adressait la relance). */
  entityLabel: string;
  /** Segment de l'entité (BPI/PR ou PI). */
  segment: AppelSegment;
}

/** Une entité jamais appelée (cible de relance du bucket « 0 appel »). */
export interface UncalledEntity {
  id: string;
  label: string;
  segment: AppelSegment;
}

/** Clé de bucket de nombre d'appels. */
export type CallBucketKey = '0' | '1' | '2' | '3plus';

/** Une ligne du tableau : un bucket de nombre d'appels. */
export interface CallBucket {
  key: CallBucketKey;
  label: string;
  /** Entités BPI/PR dans ce bucket. */
  bpiPr: number;
  /** Entités PI dans ce bucket. */
  pi: number;
  /** Total entités. */
  total: number;
  /** Détail des relances (appels) des entités du bucket — buckets ≥ 1. */
  calls: CallDetail[];
  /** Entités jamais appelées — bucket « 0 appel » uniquement. */
  uncalled: UncalledEntity[];
}

export interface AppelsData {
  buckets: CallBucket[];
  /** Total entités relançables (BPI/PR + PI). */
  totalEntities: number;
  /** Total appels rattachés à une entité de l'univers. */
  totalCalls: number;
  /** Appels consignés hors univers de relance (honnêteté seed). */
  callsOutsideUniverse: number;
}

export interface AppelsParams {
  rows: RawRows;
  edition: EditionRef;
}

/** Une entité relançable interne (avant bucketing). */
interface Entity {
  id: string;
  label: string;
  segment: AppelSegment;
}

/** Libellé court d'un équipage : n° d'équipage sinon nom sinon id court. */
function crewLabel(c: RawCrew): string {
  if (c.crew_number && c.crew_number.trim()) return `Équipage ${c.crew_number.trim()}`;
  if (c.name && c.name.trim()) return c.name.trim();
  return `Équipage ${c.id.slice(0, 8)}`;
}

/** Libellé d'un prospect : « Prénom Nom » sinon id court. */
function prospectLabel(c: RawContact): string {
  const name = [c.first_name, c.last_name]
    .filter((s) => s && s.trim())
    .join(' ')
    .trim();
  return name || `Prospect ${c.id.slice(0, 8)}`;
}

/** Libellé d'un contact membre d'équipage : « Prénom — Équipage X » sinon l'équipage. */
function memberLabel(contact: RawMemberContact | undefined, crew: RawCrew): string {
  const first = contact?.first_name?.trim();
  return first ? `${first} — ${crewLabel(crew)}` : crewLabel(crew);
}

/** Nom d'affichage d'un utilisateur (full_name sinon email). */
function userName(u: RawUser): string {
  return u.full_name?.trim() || u.email;
}

/** Classe un nombre d'appels dans un bucket. */
function bucketKeyFor(count: number): CallBucketKey {
  if (count <= 0) return '0';
  if (count === 1) return '1';
  if (count === 2) return '2';
  return '3plus';
}

const BUCKET_LABELS: Record<CallBucketKey, string> = {
  '0': '0 appel',
  '1': '1 appel',
  '2': '2 appels',
  '3plus': '3+ appels',
};

const BUCKET_ORDER: CallBucketKey[] = ['0', '1', '2', '3plus'];

/**
 * Suivi des appels par équipage/prospect — buckets 0/1/2/3+ × (BPI/PR, PI).
 * Univers = table de relance (voir en-tête). Appels comptés en cumul.
 */
export function computeAppels({ rows, edition }: AppelsParams): AppelsData {
  // 1. Univers de relance (CONTACTS, dédupliqués par contact.id) ---------------
  //    Les appels étant rattachés aux contacts, l'entité comptée est le contact.
  const entityById = new Map<string, Entity>();

  // Prospects (PR) — globaux, segment BPI/PR.
  rows.prospects.forEach((p) => {
    if (!entityById.has(p.id)) {
      entityById.set(p.id, { id: p.id, label: prospectLabel(p), segment: 'bpi_pr' });
    }
  });

  // Contacts membres d'un équipage relançable de l'édition sélectionnée.
  //   crew 'bpi_payment_pending' → BPI/PR ; crew 'pre_registered' → PI.
  const crewById = new Map(rows.crews.map((c) => [c.id, c]));
  const memberContactById = new Map((rows.memberContacts ?? []).map((c) => [c.id, c]));
  (rows.crewMembers ?? []).forEach((m) => {
    const crew = crewById.get(m.crew_id);
    if (!crew || crew.product_id !== edition.id) return;
    let segment: AppelSegment | null = null;
    if (crew.status === BPI_PENDING_STATUS) segment = 'bpi_pr';
    else if (crew.status === PI_ONLY_STATUS) segment = 'pi';
    if (!segment) return; // ID/résilié/reporté/archivé → hors relance.
    // Un contact déjà présent (prospect) reste prospect ; sinon on l'ajoute
    // comme membre d'équipage (un contact ne devrait pas être les deux).
    if (!entityById.has(m.contact_id)) {
      const label = memberLabel(memberContactById.get(m.contact_id), crew);
      entityById.set(m.contact_id, { id: m.contact_id, label, segment });
    }
  });

  const entities = [...entityById.values()];

  // 2. Appels par entité (cumul, tout l'historique) ----------------------------
  const usersById = new Map((rows.users ?? []).map((u) => [u.id, u]));
  const callsByEntity = new Map<string, CallDetail[]>();
  let totalCalls = 0;
  let callsOutsideUniverse = 0;

  (rows.calls ?? []).forEach((call: RawCall) => {
    const eid = call.entity_id ?? null;
    const ent = eid ? entityById.get(eid) : undefined;
    if (!ent) {
      // Appel sur une entité hors univers de relance (ID atteint, contact non
      // prospect, etc.) — compté à part pour l'honnêteté.
      callsOutsideUniverse += 1;
      return;
    }
    const commercial = call.created_by ? usersById.get(call.created_by) : undefined;
    const detail: CallDetail = {
      id: call.id,
      date: call.created_at,
      direction: (call.metadata?.direction ?? null) as CallDirection | null,
      outcome: (call.metadata?.outcome ?? null) as CallOutcome | null,
      commercialName: commercial ? userName(commercial) : null,
      entityLabel: ent.label,
      segment: ent.segment,
    };
    const arr = callsByEntity.get(ent.id);
    if (arr) arr.push(detail);
    else callsByEntity.set(ent.id, [detail]);
    totalCalls += 1;
  });

  // 3. Bucketing ---------------------------------------------------------------
  const buckets: Record<CallBucketKey, CallBucket> = {
    '0': {
      key: '0',
      label: BUCKET_LABELS['0'],
      bpiPr: 0,
      pi: 0,
      total: 0,
      calls: [],
      uncalled: [],
    },
    '1': {
      key: '1',
      label: BUCKET_LABELS['1'],
      bpiPr: 0,
      pi: 0,
      total: 0,
      calls: [],
      uncalled: [],
    },
    '2': {
      key: '2',
      label: BUCKET_LABELS['2'],
      bpiPr: 0,
      pi: 0,
      total: 0,
      calls: [],
      uncalled: [],
    },
    '3plus': {
      key: '3plus',
      label: BUCKET_LABELS['3plus'],
      bpiPr: 0,
      pi: 0,
      total: 0,
      calls: [],
      uncalled: [],
    },
  };

  entities.forEach((ent) => {
    const calls = callsByEntity.get(ent.id) ?? [];
    const key = bucketKeyFor(calls.length);
    const b = buckets[key];
    b.total += 1;
    if (ent.segment === 'pi') b.pi += 1;
    else b.bpiPr += 1;
    if (key === '0') {
      b.uncalled.push({ id: ent.id, label: ent.label, segment: ent.segment });
    } else {
      b.calls.push(...calls);
    }
  });

  // Détail trié : relances les plus récentes en tête ; cibles 0-appel par segment.
  (['1', '2', '3plus'] as CallBucketKey[]).forEach((k) => {
    buckets[k].calls.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  });
  buckets['0'].uncalled.sort((a, b) => {
    if (a.segment !== b.segment) return a.segment === 'bpi_pr' ? -1 : 1;
    return a.label.localeCompare(b.label, 'fr');
  });

  return {
    buckets: BUCKET_ORDER.map((k) => buckets[k]),
    totalEntities: entities.length,
    totalCalls,
    callsOutsideUniverse,
  };
}
